/**
 * PALWORLD OFFICIAL CARD GAME - Complete Game Configuration
 *
 * This is the ONLY file you need to modify for Palworld-specific settings.
 *
 * Palworld is the default game on tcgfan.com. Unlike the other games its cards
 * are not bundled as json: they are read from the shared `product_data` table
 * through tcgfan-backend, so a re-scrape shows up without a redeploy.
 */

import { PalworldHandler } from "./handler";
import {
  getCardData,
  getAACardIdData,
  getCardImage,
  getAAFromCardId,
} from "@/lib/games/palworld/getCardDataLocal";
import { CDN } from "@/lib/cdn";
import { GAME_DOMAINS } from "@/games/domains";

export const palworldGame = {
  // ============================================================================
  // BASIC INFO
  // ============================================================================
  id: "palworld" as const,
  name: "Palworld TCG",
  abbreviation: "Palworld TCG",
  gameId: 91, // Database game ID, and TCGplayer's category id

  // ============================================================================
  // SEO & METADATA
  // ============================================================================
  seo: {
    title: "Palworld TCG Card Database & Card Lists",
    description:
      "Browse the complete Palworld OFFICIAL CARD GAME database on TCGfan. Explore every card from Dawn of Palpagos and the Trial Decks, with full card lists by set, Pal types, work aptitudes and card text. Whether you are opening your first booster or hunting a specific Lucky Pal, TCGfan has the full Palworld TCG card list.",
  },

  // ============================================================================
  // GAME MECHANICS
  // ============================================================================
  mechanics: {
    hasLeader: false,
    hasLeaderDistribution: false,
    hasPieChart: true,
    hasFormatReleaseDates: false,
    maxCopiesDefault: 4,
    hasLifeProperty: false,
    leaderType: "Pal",
  },

  // Category 91 on TCGplayer. Unlike the bundled games, the card -> product id
  // mapping is not in a local aa_data.json: it is joined on from
  // /api/data/aa-data when the cards are fetched, and rides on each card as
  // `tcgplayer_id`.
  tcgplayer: {
    categoryId: 91,
    productLine: "Palworld OFFICIAL CARD GAME",
  },

  routePrefix: "palworld/",

  // ============================================================================
  // FORMATS & SETS
  // ============================================================================
  // No decklists are collected for Palworld yet, so this is the card pool
  // rather than a competitive format -- the current booster set. Every game
  // must declare at least one (see __tests__/gameConformance.test.ts), and the
  // decklist tabs read from here.
  //
  // The set list for *card browsing* is not this: it is derived from the cards
  // themselves in lib/games/palworld/getCardData.ts, so a new set appears
  // without an edit here.
  formats: {
    global: ["EBP01"],
    east: [] as string[],
  },

  submitFormUrl: undefined as string | undefined,

  // ============================================================================
  // NAVIGATION & UI
  // ============================================================================
  navigation: {
    /** The game's deck builder, or undefined if it has none yet. */
    deckbuilderHref: "/palworld/deckbuilder?layout=edit" as string | undefined,
    showMarketWatch: true,
    marketWatchHref: "/palworld/market-watch",
    showTools: false,
    // Card browsing is the point of the section, so the set menus are on.
    showBoosters: true,
    showStarters: true,
    showSearch: false,
  },

  // ============================================================================
  // DOMAIN CONFIGURATION
  // ============================================================================
  domain: {
    ...GAME_DOMAINS["palworld"],
    disabledRoutes: [] as string[],
  },

  // ============================================================================
  // GAME HANDLER & DECK BUILDER
  // ============================================================================
  handler: () => new PalworldHandler(),

  deckBuilder: {
    getCardData,
    getAACardIdData,
    getCardImage,
    getAAFromCardId,
  },

  // ============================================================================
  // CDN CONFIGURATION
  // ============================================================================
  // Palworld has no leaders; this is here for interface parity.
  cdn: {
    leaderSquaresPath: `${CDN}/games/palworld/images/leader-squares`,
  },

  // ============================================================================
  // INITIAL STATE FOR STORES (auto-generated, no need to modify)
  // ============================================================================
  initialState: {
    prices: null,
    loading: false,
    decks: [],
    cardOrderIds: [],
    filters: {},
  },
};

export type PalworldGame = typeof palworldGame;
